import React from 'react'
import { dropdownBaseStyle, dropdownItemStyle } from '../simple/dropdown'
import { Tooltip } from '../simple/tooltip'

type BaseUser = {
  name: string
  type?: 'student' | 'guardian' | 'teacher'
  extraNameInfo?: string
  exactMatchSearchVals?: string[]
  sortPriority?: number
  tooltip?: string
  notToggleable?: boolean
}

export type BasicUserGroup = BaseUser & {
  group: true
  groupID: string | number
  memberIDs?: number[]
}

export type BasicUser = BaseUser & {
  group?: undefined
  id: number
}

export type BasicUserOrGroup = BasicUserGroup | BasicUser

export type NeededUser = {
  id?: number
  groupID?: string | number
}

const getUserColor = (user: BasicUserOrGroup, focused: boolean = false): string => {
  if (user.notToggleable) {
    return 'bg-charcoal-light/85 text-white'
  }
  const baseStyle = 'text-white cursor-pointer'
  const color = user.group
    ? focused
      ? 'bg-charcoal-dark'
      : 'bg-charcoal-active'
    : focused
      ? 'bg-charcoal-hover'
      : 'bg-charcoal-light'
  return `${color} ${baseStyle}`
}

export const UserBadge: React.FC<{
  user: BasicUserOrGroup
  onClick?: () => void
  showDefaultTooltip?: boolean
}> = ({ user, onClick, showDefaultTooltip }) => {
  const htmlTooltip = React.useMemo(
    () => (user.tooltip ? <div dangerouslySetInnerHTML={{ __html: user.tooltip }} /> : undefined),
    [user.tooltip]
  )
  return (
    <Tooltip text={showDefaultTooltip ? htmlTooltip : undefined}>
      <div
        className={`${getUserColor(user)} flex select-none items-center text-nowrap rounded-full px-3 pb-[.2rem] pt-[.3rem] text-sm`}
        onClick={onClick}
      >
        {user.name}
      </div>
    </Tooltip>
  )
}

export const PermenantUserBadge: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  return (
    <div className="select-none text-nowrap rounded-full bg-charcoal-light/85 px-3 pb-[.2rem] pt-[.3rem] text-sm text-white">
      {children}
    </div>
  )
}

export const UserDropdownItem: React.FC<{
  user: BasicUserOrGroup
  selected: boolean
  onClick: () => void
  doubleClickAction?: () => void
  focused: boolean
  greyedOut: boolean
  tooltipOverride?: string
  showTypeIndicator?: boolean
}> = ({ user, selected, onClick, focused, greyedOut, doubleClickAction, tooltipOverride, showTypeIndicator }) => {
  const colors = selected ? getUserColor(user, focused) : focused ? 'bg-gray-100' : dropdownItemStyle
  const disabled = greyedOut && !selected ? 'opacity-50 !cursor-default' : ''

  const htmlTooltip = React.useMemo(
    () => (user.tooltip ? <div dangerouslySetInnerHTML={{ __html: user.tooltip }} /> : undefined),
    [user.tooltip]
  )
  const tooltip = tooltipOverride ?? htmlTooltip
  const item = (
    <div
      className={`${dropdownBaseStyle} ${colors} ${disabled} select-none justify-between`}
      onClick={(e) => {
        if (greyedOut) return
        if (e.detail === 2 && doubleClickAction) doubleClickAction()
        else onClick()
      }}
      ref={focused ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
    >
      {user.name}
      {user.extraNameInfo ? ` ${user.extraNameInfo}` : null}
      {showTypeIndicator && (
        <Tooltip text={typeToLabel(user.type)}>
          <div className={`-mr-[.3rem] w-1.5 self-stretch rounded ${typeToColor(user.type)}`} />
        </Tooltip>
      )}
    </div>
  )
  return tooltip ? <Tooltip text={tooltip}>{item}</Tooltip> : item
}

export const typeToColor = (type: BasicUserOrGroup['type']) =>
  type === undefined
    ? 'bg-white'
    : {
        student: 'bg-student',
        guardian: 'bg-guardian',
        teacher: 'bg-text',
      }[type]

export const typeToLabel = (type: BasicUserOrGroup['type']) =>
  type === undefined
    ? 'Unknown'
    : {
        student: 'Student',
        guardian: 'Guardian',
        teacher: 'Staff',
      }[type]

export const UserDropdownInfo: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className={`${dropdownBaseStyle} text-sm`}>{children}</div>
}

export const mergeUsers = <T extends BasicUserOrGroup>(users: T[], newUsers: T[]): T[] => {
  const seenIDs = new Set<number>()
  const seenGroupIDs = new Set<string | number>()
  const mergedUsers: T[] = []
  for (const r of [...users, ...newUsers]) {
    const idKey = r.group ? r.groupID : r.id
    const seenSet = r.group ? seenGroupIDs : seenIDs
    if (!seenSet.has(idKey)) {
      seenSet.add(idKey)
      mergedUsers.push(r)
    }
  }
  return mergedUsers
}

export const preferExistingUser = <T extends BasicUserOrGroup>(existing: T[], takeUsers: T[]): T[] => {
  const takenIDs = new Set<number>()
  const takenGroupIDs = new Set<string | number>()
  for (const u of takeUsers) {
    if (u.group) takenGroupIDs.add(u.groupID)
    else takenIDs.add(u.id)
  }
  const mergedUsers: T[] = []
  for (const u of existing) {
    if (u.group && takenGroupIDs.has(u.groupID)) {
      mergedUsers.push(u)
      takenGroupIDs.delete(u.groupID)
    } else if (!u.group && takenIDs.has(u.id)) {
      mergedUsers.push(u)
      takenIDs.delete(u.id)
    }
  }
  for (const u of takeUsers) {
    if (u.group && takenGroupIDs.has(u.groupID)) mergedUsers.push(u)
    else if (!u.group && takenIDs.has(u.id)) mergedUsers.push(u)
  }
  return mergedUsers
}

export const makeUsersUnique = <T extends BasicUserOrGroup>(users: T[]): T[] => {
  return mergeUsers([], users)
}
